import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ami/react/core';
import { Machine } from '../../../machines/machine';
import {
  Carrier,
  FlatWeightCategory,
} from '../../../products/productConfiguration';
import { Quote } from '../../../quotes/quote';
import { QuoteProductConfiguration } from '../../../quotes/quoteProductConfiguration';
import { quoteCreationType, QuoteProduct } from '../../../quotes/otherQuoteEntities';
import QuoteWizardContext from '../../../quotes/QuoteWizardContext';
import { QuoteProductDto } from '../../../quotes/otherQuoteEntities';
import IncompatibleStockItemsModal from '../../../quotes/IncompatibleStockItemsModal';
import ReviewQuoteMachineModal from './ReviewQuoteMachineModal';
import { updateQuotePlusLeadTimesAndProducts } from '../../../quotes/quoteUpdater';
import { OemThumbMountType } from '../../../machines/oemThumbMountType';

const DataItem = ({ label, children }) => (
  <div className="row">
    <label className="col-sm-4 col-form-label">{label}</label>
    <div className="col-sm-8">
      <div>{children}</div>
    </div>
  </div>
);

export default class ReviewQuoteMachineComponent extends React.Component {
  state = {
    showModal: false,
    loadingNewConfiguration: false,
    showIncompatibleStockItemsModal: false,
    incompatibleStockItems: [],
  };
  static contextType = QuoteWizardContext;
  static propTypes = {
    quote: PropTypes.instanceOf(Quote).isRequired,
    onUpdate: PropTypes.func.isRequired,
    machines: PropTypes.arrayOf(PropTypes.instanceOf(Machine)).isRequired,
    preferredMakeIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    carriers: PropTypes.arrayOf(PropTypes.instanceOf(Carrier)),
    weightCategories: PropTypes.arrayOf(
      PropTypes.instanceOf(FlatWeightCategory)
    ),
    isFromStep3: PropTypes.bool,
    quoteProductDto: PropTypes.instanceOf(QuoteProductDto),
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration)
      .isRequired,
    onQuoteProductDtoChange: PropTypes.func,
    showNonModalContent: PropTypes.bool,
    onloadSharedDocuments: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
    isFromStep3InventoryOnly: PropTypes.bool
  };

  static defaultProps = {
    carriers: [],
    weightCategories: [],
    isFromStep3: false,
    showNonModalContent: true,
  };

  constructor(props) {
    super(props);
    this.saved = props.onSaved;
  }

  hide = () => this.setState({ showModal: false });

  save = async (make, machineId, model, onSaved) => {
    this.setState({ loadingNewConfiguration: true });
    try {
      const updatedQuote = await this.updateMachine(make, machineId, model);
      await this.updateQuote(updatedQuote);
      this.setState({ loadingNewConfiguration: false });
      this.hide();

      if (this.saved) {
        this.saved();
      }
    } catch (error) {
      this.setState({ loadingNewConfiguration: false });
    }
  };

  saved = () => { };

  updateMachine = async (make, machineId, model) => {
    const { machines } = this.props;
    const updatedQuote = this.props.quote.clone();
    updatedQuote.step1.manufacturer = make;
    updatedQuote.step1.machineId = machineId;
    updatedQuote.step1.model = model;
    updatedQuote.step1.type = quoteCreationType.byMakeAndModel;
    const machine = await updatedQuote.validateStep1();
    updatedQuote.setMachine(machine);

    const machineModel = machines.find((x) => x.id === machineId);
    if (machineModel) {
      const attachmentPointsFiltered =
        machineModel.attachmentPointConfigurations.filter(
          (x) =>
            updatedQuote.attachmentPoints
              .map((x) => x.id)
              .includes(x.attachmentPointId) && x.thumbConfiguration !== null
        );
      attachmentPointsFiltered.forEach((attachmentPoint) => {
        if (
          attachmentPoint.thumbConfiguration.oemThumbMount ===
          OemThumbMountType.NeverHasOemThumbMount
        ) {
          updatedQuote.updateAttachmentPointsThumbMount(
            attachmentPoint,
            'DoesNotHaveThumbMount'
          );
        } else if (
          attachmentPoint.thumbConfiguration.oemThumbMount ===
          OemThumbMountType.AlwaysHasOemThumbMount
        ) {
          updatedQuote.updateAttachmentPointsThumbMount(
            attachmentPoint,
            'HasThumbMount'
          );
        }
      });
    }

    this.props.onUpdate(updatedQuote);
    return updatedQuote;
  };

  updateQuote = async (quote) => {
    const {
      onUpdate,
      isFromStep3,
      quoteProductDto,
      onQuoteProductDtoChange,
      onloadSharedDocuments,
      quoteProductConfiguration,
      isFromStep3InventoryOnly
    } = this.props;

    if (isFromStep3 && !quoteProductDto.productId && !quoteProductDto.inventoryItemId)
      return;

    updateQuotePlusLeadTimesAndProducts(
      quote,
      quote,
      quoteProductConfiguration,
      onUpdate,
      false,
      isFromStep3,
      quoteProductDto,
      onQuoteProductDtoChange,
      this.context.onQuoteProductConfigurationUpdate,
      (newState) => this.setState(newState),
      onloadSharedDocuments,
      isFromStep3InventoryOnly
    );
  };

  getThumbConfiguration() {
    const { quote, machines } = this.props;
    const { attachmentPoints } = quote;

    const machine = machines.find((x) => x.id === quote.step1.machineId);

    if (machine) {
      const attachmentPointsFiltered =
        machine.attachmentPointConfigurations.filter((x) =>
          attachmentPoints.map((x) => x.id).includes(x.attachmentPointId)
        );
      const attachmentPoint = attachmentPointsFiltered.find(
        (x) => x.thumbConfiguration !== null
      );
      return attachmentPoint ? attachmentPoint.thumbConfiguration : null;
    }
    return null;
  }

  render() {
    const {
      showModal,
      loadingNewConfiguration,
      showIncompatibleStockItemsModal,
      incompatibleStockItems,
    } = this.state;
    const {
      quote,
      carriers,
      weightCategories,
      machines,
      preferredMakeIds,
      isFromStep3,
      showNonModalContent,
    } = this.props;
    const carrier = carriers.find((x) => x.id === quote.step1.carrierId);
    const weightCategory = weightCategories.find(
      (x) => x.id === quote.step1.weightCategoryId
    );
    const machine = machines.find((x) => x.id === quote.step1.machineId);
    const showRequiredForInventoryItemBanner = !machine && isFromStep3;

    const hasInventoryItemAdded = quote.products?.some(x => !!x.inventoryItemId);
    const isEditable = !isFromStep3 || quote.products.length === 0 || !hasInventoryItemAdded;

    return (
      <div className="card-deck">
        {showNonModalContent && (
          <div className={`card mb-4 box-shadow quote-info ${isFromStep3 && !machine ? 'invalid' : ''}`}>
            <div className="card-body p-3">
              {carrier && weightCategory && (
                <Fragment>
                  <h6 className="card-title text-center">
                    Machine Information
                  </h6>
                  <DataItem label="Carrier:">
                    <div className="mt-2">{carrier.name}</div>
                  </DataItem>
                  <DataItem label="Category:">
                    <div className="mt-2">
                      {weightCategory.label} ({weightCategory.description})
                    </div>
                  </DataItem>
                </Fragment>
              )}
              {showRequiredForInventoryItemBanner && (
                <div className="alert alert-danger text-center p-2 mb-0">
                  <strong>
                    Required in order to add a stock item to the quote
                  </strong>
                  <br />
                </div>
              )}
              <DataItem label="Machine:">
                {quote.step1.machineId && (
                  <div className="mt-2">
                    {quote.step1.manufacturer} {quote.step1.model}
                  </div>
                )}
                {!quote.step1.machineId && (
                  <div className="mt-2">Not selected</div>
                )}
              </DataItem>
              {isEditable && (
                <Button
                  label={machine ? 'Change Machine' : 'Select Machine'}
                  className="btn btn-outline-primary mt-1 offset-4"
                  onClick={() => this.setState({ showModal: true })}
                />
              )}
              {isEditable && (
                <ReviewQuoteMachineModal
                  manufacturer={quote.step1.manufacturer}
                  machineId={quote.step1.machineId}
                  weightCategoryId={quote.step1.weightCategoryId}
                  show={showModal}
                  onSave={this.save}
                  onHide={this.hide}
                  preferredMakeIds={preferredMakeIds}
                  loadingNewConfiguration={loadingNewConfiguration}
                />
              )}
            </div>
          </div>
        )}
        {showIncompatibleStockItemsModal && (
          <IncompatibleStockItemsModal
            incompatibleStockItems={incompatibleStockItems}
            onCloseClicked={() =>
              this.setState({ showIncompatibleStockItemsModal: false })
            }
          />
        )}
      </div>
    );
  }
}
